import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import type {BzDay} from '@bringoz/ui';
import * as moment from 'moment-timezone';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../../commons/services/split-service.service';

const DAY_FORMAT = 'YYYY-MM-DD';

/**
 * Day and time of a stop or break, both read and written in one timezone - `zoneId`, or the user's
 * selected one - so a time past midnight lands on the day the user picked, not the timecard's day.
 * The calendar is limited to the days between `minTime` and `maxTime` - today while there is no
 * `maxTime`, as a timecard not clocked out yet ends now.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-date-time-field',
  templateUrl: './timecard-date-time-field.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardDateTimeFieldComponent {

  readonly timeStamp = input<number | null>();
  /** Day used while `timeStamp` is empty. */
  readonly referenceDate = input<number>();
  readonly zoneId = input<string>();
  readonly minTime = input<number>();
  readonly maxTime = input<number>();

  readonly timeChange = output<number>();

  private readonly splitService = injectLegacy<SplitService>('splitService');

  readonly dateFormat: string = this.splitService.getDateAndTimeConfig().fullDate || 'MM/DD/YYYY';

  readonly baseTime = computed(() => this.timeStamp() || this.referenceDate());
  readonly day = computed(() => this.toDay(this.baseTime()));
  readonly minDay = computed(() => this.toDay(this.minTime()));
  readonly maxDay = computed(() => this.toDay(this.maxTime() || Date.now()));

  /** Moves the time to the picked day, keeping its hour and minute. */
  onDayChange(day: BzDay | null) {
    const baseTime = this.baseTime();
    if (!day || !baseTime) {
      return;
    }
    const picked = moment(day, DAY_FORMAT);
    this.timeChange.emit(moment.tz(baseTime, this.timeZone())
      .set({year: picked.year(), month: picked.month(), date: picked.date()})
      .valueOf());
  }

  /**
   * bz-form-field wraps its content in a <label>, and a click anywhere inside a label is passed on to
   * its first input - the date picker's, which opens the calendar. The hour and minute selects have no
   * input of their own, so their clicks would open it too.
   */
  onTimeClick(event: MouseEvent) {
    event.preventDefault();
  }

  private toDay(timestamp: number): BzDay | null {
    return timestamp ? moment.tz(timestamp, this.timeZone()).format(DAY_FORMAT) : null;
  }

  // Read live - the user may switch timezone while the screen is open
  private timeZone(): string {
    return this.zoneId() || this.splitService.getTimeZone();
  }
}
